import React, { useState, useEffect } from 'react';
import { Star, MapPin, Calendar, Gift, Users, Building, Activity, Facebook, Instagram, Download } from 'lucide-react';
import InteractiveMap from './InteractiveMap';
import VisualAdventCalendar from './VisualAdventCalendar';
import { mockData } from '../mockData';

const Home = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const scrollToMap = () => {
    document.getElementById('calendar-map').scrollIntoView({ 
      behavior: 'smooth' 
    });
  };

  return (
    <div className="min-h-screen">
      {/* Header / Hero Section */}
      <header className="hero-section">
        <div className="container text-center relative z-10">
          {/* Social Media Links */}
          <div className="absolute top-8 right-8 flex gap-4 z-20">
            <a 
              href={mockData.socialMedia.facebook}
              className="text-gold hover:text-gold-light transition-colors p-2 bg-white/10 rounded-full backdrop-blur-sm"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <a 
              href={mockData.socialMedia.instagram}
              className="text-gold hover:text-gold-light transition-colors p-2 bg-white/10 rounded-full backdrop-blur-sm"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Instagram className="w-5 h-5" />
            </a>
          </div>

          <div className="animate-float mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_4e5397d3-7819-4499-9fa9-6acd74b22875/artifacts/fcyppy6t_rozsvit_logo.png" 
              alt="Rozsviť Přerov Logo" 
              className="mx-auto w-32 h-32 mb-6 drop-shadow-2xl"
            />
          </div>
          
          <h1 className="hero-title font-serif">
            ROZSVIŤ PŘEROV
          </h1>
          
          <div className="max-w-2xl mx-auto mb-8">
            <p className="text-2xl font-serif text-center text-gold/90 italic font-light leading-relaxed">
              „Vánoce jsou kouzelné. Rozsviťme naše město, ať září jako nikdy předtím."
            </p>
          </div>
          
          <button 
            onClick={scrollToMap}
            className="btn-gold animate-pulse-gold"
          >
            <Calendar className="w-5 h-5" />
            Objevte kalendář
          </button>
        </div>
      </header>

      {/* About Section */}
      <section className="section-padding bg-dark-blue">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif font-bold text-gold mb-6">
              Živý adventní kalendář
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="text-lg text-white/90 mb-8 leading-relaxed space-y-4">
                <p>
                  Od 1. 12. do 24. 12. se každý den v Přerově rozsvítí jedno velké číslo v některém významném subjektu či instituci. 
                  Každé odpoledne v 16:01 najdete na našem webu indicii na další den a ve 12:00 odhalíme přesnou lokaci. V 16:00 se číslo rozsvítí a čeká vás překvapení – 
                  zpívání koled, svařák, dílničky a programy.
                </p>
                
                <div className="bg-gold/10 rounded-xl p-6 border border-gold/30">
                  <h4 className="text-xl font-serif text-gold mb-4">Příběh projektu</h4>
                  <p className="mb-4">
                    První myšlenka s projektem „Rozsviť Přerov" se zrodila již před rokem. Od té doby jsme usilovně pracovali na organizaci tohoto unikátního projektu. Díky pozitivním reakcím a spolupráci oslovených subjektů se nám tak letos poprvé v historii města Přerova podaří jedinečný okamžik..a to propojení všech významných subjektů města Přerova a zapojení široké veřejnosti se společným cílem objevovat město, poznávat jeho významná místa, trávit spolu čas a věnovat se aktivnímu pohybu.
                  </p>
                  <p className="mb-4">
                    Je to akce, na které nezáleží, kdo přišel sám a kdo s celou skupinou – všichni máme společnou cestu a společný okamžik, který sdílíme tady a teď. K akci také vznikla píseň „Rozsviť město", která bude každý den všechna čísla provázet.
                  </p>
                  <p className="text-gold font-semibold">
                    Moc se těšíme na odhalení všech čísel a pevně věříme, že si tuto novou přerovskou akci užijete plnými doušky.
                  </p>
                  <p className="text-gold/80 text-sm mt-4">
                    <span className="font-semibold">Iveta a Petra</span><br/>
                    <span className="text-xs">Autorky projektu Rozsviť Přerov</span>
                  </p>
                </div>
              </div>
              
              {(() => {
                // Get current Czech time
                const getCzechTime = () => {
                  return new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" });
                };

                const czechTimeString = getCzechTime();
                const czechTime = new Date(czechTimeString);
                const currentDay = czechTime.getDate();
                const currentMonth = czechTime.getMonth();

                // Find current day's data
                let todayData = null;
                if (currentMonth === 11 && currentDay <= 24) { // December and valid advent day
                  todayData = mockData.calendarDays.find(d => d.day === currentDay);
                }

                if (!todayData) {
                  return (
                    <div className="text-center">
                      <p className="text-white/70">Adventní kalendář začíná 1. prosince!</p>
                    </div>
                  );
                }

                // Check if day should be revealed (12:00)
                const revealDate = new Date();
                revealDate.setFullYear(czechTime.getFullYear(), 11, currentDay);
                revealDate.setHours(12, 0, 0, 0);
                const revealDateCzech = new Date(revealDate.toLocaleString("en-US", { timeZone: "Europe/Prague" }));
                const isRevealed = czechTime >= revealDateCzech;

                // Check if clue should be shown (16:01 of previous day)
                const clueDate = new Date();
                clueDate.setFullYear(czechTime.getFullYear(), 11, currentDay - 1);
                clueDate.setHours(16, 1, 0, 0);
                const clueDateCzech = new Date(clueDate.toLocaleString("en-US", { timeZone: "Europe/Prague" }));
                const showClue = czechTime >= clueDateCzech;

                return (
                  <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                    {/* Daily Clue */}
                    <div className="glass rounded-2xl p-6">
                      <div className="flex items-center justify-center gap-2 mb-4">
                        <Star className="w-6 h-6 text-gold animate-twinkle" />
                        <h3 className="text-xl font-serif font-bold text-gold">Denní indicie</h3>
                        <Star className="w-6 h-6 text-gold animate-twinkle" />
                      </div>
                      <div className="bg-gold/10 rounded-lg p-4 border border-gold/30">
                        <p className="text-gold font-semibold mb-2">{currentDay}. DEN</p>
                        {showClue ? (
                          <p className="text-white italic">"{todayData.clue}"</p>
                        ) : (
                          <p className="text-white/50 italic">Indicie se objeví v 16:01 předchozího dne</p>
                        )}
                      </div>
                    </div>

                    {/* Daily Mystery */}
                    <div className="glass rounded-2xl p-6">
                      <div className="flex items-center justify-center gap-2 mb-4">
                        <Gift className="w-6 h-6 text-gold animate-twinkle" />
                        <h3 className="text-xl font-serif font-bold text-gold">Denní tajemství</h3>
                        <Gift className="w-6 h-6 text-gold animate-twinkle" />
                      </div>
                      <div className="bg-gold/10 rounded-lg p-4 border border-gold/30">
                        <p className="text-gold font-semibold mb-2">{czechTime.getDate()}. prosince 2025</p>
                        {isRevealed ? (
                          <div>
                            <p className="text-white mb-2">{todayData.location}</p>
                            <div className="inline-flex items-center px-3 py-1 bg-gold text-dark-blue rounded-full text-sm font-semibold">
                              ✨ Lokace odhalena! Rozsvícení v 16:00
                            </div>
                          </div>
                        ) : (
                          <div>
                            <p className="text-white/70 mb-2">Lokace se odhalí ve 12:00</p>
                            <div className="inline-flex items-center px-3 py-1 bg-gray-600 text-white rounded-full text-sm">
                              🕐 Čekáme na odhalení
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </section>

      {/* Goals Section */}
      <section className="section-padding">
        <div className="container">
          <h2 className="text-4xl font-serif font-bold text-gold text-center mb-16">
            Cíle akce
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {mockData.goals.map((goal, index) => (
              <div key={index} className="feature-card text-center">
                <div className="text-6xl mb-6">{goal.icon}</div>
                <h3 className="text-xl font-serif font-bold text-gold mb-4">
                  {goal.title}
                </h3>
                <p className="text-white/80 mb-4">
                  {goal.description}
                </p>
                <p className="text-white/70 text-sm leading-relaxed text-left">
                  {goal.details}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Event Start Section */}
      <section className="section-padding bg-gradient-to-b from-dark-blue to-blue-900">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-serif font-bold text-gold mb-8">
              Začínáme 1. prosince U Hvězdárny!!!!
            </h2>
            
            <div className="glass rounded-2xl p-8 mb-8">
              <div className="flex justify-center mb-6">
                <div className="bg-gold rounded-full p-4">
                  <Star className="w-8 h-8 text-dark-blue" />
                </div>
              </div>
              
              <div className="text-lg text-white/90 leading-relaxed space-y-4">
                <p>
                  První místo bude odhaleno jako slavnostní zahájení. Aktivitka z.s. společně s panem primátorem Petrem Vránou zahájí tuto akci.
                </p>
                <p>
                  Vysvitne také vánoční hvězdička Třpytka (maskot), která bude celý živý adventní kalendář provázet. Pro děti bude připraven program a pro dospělé něco malého na zahřátí.
                </p>
                <p>
                  Pro veřejnost bude také k dispozici prohlídka Hvězdárny.
                </p>
              </div>
              
              <div className="flex flex-wrap justify-center gap-4">
                <div className="flex items-center gap-2 text-gold">
                  <MapPin className="w-5 h-5" />
                  <span>Hvězdárna Přerov</span>
                </div>
                <div className="flex items-center gap-2 text-gold">
                  <Calendar className="w-5 h-5" />
                  <span>1. prosince 2025</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Advent Calendar Section */}
      <section className="section-padding bg-gradient-to-b from-dark-blue to-blue-900">
        <div className="container">
          <VisualAdventCalendar calendarDays={mockData.calendarDays} />
        </div>
      </section>

      {/* Interactive Map Section */}
      <section id="calendar-map" className="section-padding">
        <div className="container">
          <h2 className="text-4xl font-serif font-bold text-gold text-center mb-16">
            Mapa Přerova – Živý kalendář
          </h2>
          
          <div className="mb-8">
            <InteractiveMap 
              calendarDays={mockData.calendarDays}
              currentDay={mockData.currentDay}
            />
          </div>
          
          {(() => {
            // Get current Czech time
            const getCzechTime = () => {
              return new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" });
            };

            const czechTimeString = getCzechTime();
            const czechTime = new Date(czechTimeString);
            const currentDay = czechTime.getDate();
            const currentMonth = czechTime.getMonth();

            // Find current day's data
            let todayData = null;
            if (currentMonth === 11 && currentDay <= 24) { // December and valid advent day
              todayData = mockData.calendarDays.find(d => d.day === currentDay);
            }

            if (!todayData) {
              return (
                <div className="text-center glass rounded-2xl p-6 max-w-2xl mx-auto">
                  <p className="text-white/70">Adventní kalendář začíná 1. prosince!</p>
                </div>
              );
            }

            // Check if day should be revealed (12:00)
            const revealDate = new Date();
            revealDate.setFullYear(czechTime.getFullYear(), 11, currentDay);
            revealDate.setHours(12, 0, 0, 0);
            const revealDateCzech = new Date(revealDate.toLocaleString("en-US", { timeZone: "Europe/Prague" }));
            const isRevealed = czechTime >= revealDateCzech;

            // Check if clue should be shown (16:01 of previous day)
            const clueDate = new Date();
            clueDate.setFullYear(czechTime.getFullYear(), 11, currentDay - 1);
            clueDate.setHours(16, 1, 0, 0);
            const clueDateCzech = new Date(clueDate.toLocaleString("en-US", { timeZone: "Europe/Prague" }));
            const showClue = czechTime >= clueDateCzech;

            return (
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {/* Daily Clue for Map */}
                <div className="glass rounded-2xl p-6 text-center">
                  <h3 className="text-xl font-serif font-bold text-gold mb-4">
                    Denní indicie
                  </h3>
                  <div className="bg-gold/10 rounded-lg p-4 border border-gold/30">
                    <p className="text-gold font-semibold mb-2">{currentDay}. DEN</p>
                    {showClue ? (
                      <p className="text-white/90 italic text-lg">"{todayData.clue}"</p>
                    ) : (
                      <p className="text-white/50 italic">Indicie se objeví v 16:01 předchozího dne</p>
                    )}
                  </div>
                </div>

                {/* Mystery Reveal */}
                <div className="glass rounded-2xl p-6 text-center">
                  <h3 className="text-xl font-serif font-bold text-gold mb-4">
                    Denní tajemství
                  </h3>
                  <div className="bg-gold/10 rounded-lg p-4 border border-gold/30">
                    <p className="text-gold font-semibold mb-2">{czechTime.getDate()}. prosince 2025</p>
                    {isRevealed ? (
                      <div>
                        <p className="text-white/90 mb-2 text-lg font-semibold">{todayData.location}</p>
                        <div className="inline-flex items-center px-3 py-1 bg-gold text-dark-blue rounded-full text-sm font-semibold">
                          ✨ Lokace odhalena! Rozsvícení v 16:00
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-white/70 mb-2 text-lg">Lokace se odhalí ve 12:00</p>
                        <div className="inline-flex items-center px-3 py-1 bg-gray-600 text-white rounded-full text-sm">
                          🕐 Čekáme na odhalení...
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* QR Code Game Section */}
      <section className="section-padding bg-gradient-to-b from-dark-blue to-blue-900">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-serif font-bold text-gold mb-8">
              Sbírej QR kódy a vyhraj
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="space-y-6">
                <div className="feature-card">
                  <div className="flex justify-center mb-4">
                    <div className="bg-gold rounded-full p-3">
                      <Gift className="w-8 h-8 text-dark-blue" />
                    </div>
                  </div>
                  <p className="text-lg text-white/90 mb-4">
                    {mockData.qrGame.description}
                  </p>
                  <p className="text-gold font-semibold">
                    {mockData.qrGame.requirement}
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-2xl font-serif font-bold text-gold">Výhry čekají</h3>
                {mockData.qrGame.prizes.map((prize, index) => (
                  <div key={index} className="flex items-center gap-3 text-white/90">
                    <Star className="w-5 h-5 text-gold" />
                    <span>{prize}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Children's Section */}
      <section className="section-padding">
        <div className="container">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-4xl font-serif font-bold text-gold mb-8">
              {mockData.childrenSection.title}
            </h2>
            <p className="text-2xl font-serif text-gold/90 italic mb-6">
              {mockData.childrenSection.subtitle}
            </p>
            <p className="text-lg text-white/80 mb-12 max-w-3xl mx-auto">
              {mockData.childrenSection.description}
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              {mockData.childrenSection.downloads.map((item, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-gold/20 hover:border-gold/50 transition-all hover:scale-105">
                  <div className="mb-6">
                    <img 
                      src={item.image}
                      alt={item.name}
                      className="w-full h-48 object-cover rounded-lg shadow-md cursor-pointer"
                      onClick={() => window.open(item.downloadUrl, '_blank')}
                    />
                  </div>
                  <h3 className="text-xl font-serif font-bold text-gold mb-3">
                    {item.name}
                  </h3>
                  <p className="text-white/70 mb-6 text-sm">
                    {item.description}
                  </p>
                  <a 
                    href={item.downloadUrl}
                    className="btn-gold"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download className="w-5 h-5" />
                    {item.name.includes('pohádka') ? 'Stáhnout PDF' : 'Otevřít k tisku'}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Social Media Section */}
      <section className="section-padding bg-gradient-to-b from-dark-blue to-blue-900">
        <div className="container">
          <h2 className="text-4xl font-serif font-bold text-gold text-center mb-8">
            Sledujte nás online
          </h2>
          <p className="text-center text-white/80 mb-12 text-lg">
            Nenechte si ujít žádnou novinku a buďte součástí naší vánoční komunity
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <a 
              href={mockData.socialMedia.facebook}
              className="feature-card text-center hover:scale-105 transition-all hover:border-blue-400"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Facebook className="w-16 h-16 text-blue-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Facebook</h3>
              <p className="text-white/70 mb-4">Sledujte aktuality a fotky z akcí</p>
              <div className="inline-flex items-center text-blue-400 font-semibold">
                Sledovat stránku
                <Star className="w-4 h-4 ml-2" />
              </div>
            </a>
            
            <a 
              href={mockData.socialMedia.instagram}
              className="feature-card text-center hover:scale-105 transition-all hover:border-pink-400"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Instagram className="w-16 h-16 text-pink-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Instagram</h3>
              <p className="text-white/70 mb-4">Stories a fotky z rozsvícených míst</p>
              <div className="inline-flex items-center text-pink-400 font-semibold">
                Sledovat profil
                <Star className="w-4 h-4 ml-2" />
              </div>
            </a>
            
            <a 
              href={mockData.socialMedia.facebookEvent}
              className="feature-card text-center hover:scale-105 transition-all hover:border-gold"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Calendar className="w-16 h-16 text-gold mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">FB Událost</h3>
              <p className="text-white/70 mb-4">Přidat si akci do kalendáře</p>
              <div className="inline-flex items-center text-gold font-semibold">
                Účastnit se
                <Star className="w-4 h-4 ml-2" />
              </div>
            </a>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <div className="glass rounded-2xl p-6 max-w-2xl mx-auto">
              <h4 className="text-xl font-serif font-bold text-gold mb-4">
                Sdílejte své zážitky!
              </h4>
              <p className="text-white/80 mb-4">
                Používejte hashtag <span className="text-gold font-semibold">#RozsvitPrerov</span> a označte nás ve svých příspěvcích
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Organizers Section */}
      <section className="section-padding">
        <div className="container">
          <h2 className="text-4xl font-serif font-bold text-gold text-center mb-12">
            Organizátoři
          </h2>
          
          <div className="max-w-5xl mx-auto">
            {mockData.organizers.map((organizer, index) => (
              <div key={index} className="mb-12">
                <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-6">
                  <img 
                    src={organizer.logo} 
                    alt={organizer.name} 
                    className="w-24 h-24 object-contain flex-shrink-0"
                  />
                  <div className="text-center md:text-left">
                    <h3 className="text-2xl font-serif font-bold text-gold mb-2">{organizer.name}</h3>
                    <p className="text-white/90 font-medium text-lg mb-4">{organizer.description}</p>
                  </div>
                </div>
                
                <div className="text-white/80 leading-relaxed space-y-4 text-center md:text-left">
                  {organizer.fullDescription.split('\n\n').map((paragraph, pIndex) => (
                    <p key={pIndex}>{paragraph}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Partners Section */}
      <section className="section-padding bg-gradient-to-b from-dark-blue to-blue-900">
        <div className="container">
          <h2 className="text-4xl font-serif font-bold text-gold text-center mb-12">
            Hlavní partneři
          </h2>
          
          <div className="flex justify-center items-center gap-12 flex-wrap">
            {mockData.partners.map((partner, index) => (
              <div key={index} className="text-center">
                <div className="bg-white rounded-lg p-6 shadow-md mb-4 flex items-center justify-center min-h-[100px] min-w-[160px]">
                  <img 
                    src={partner.logo} 
                    alt={partner.name} 
                    className="max-h-16 max-w-full w-auto object-contain"
                  />
                </div>
                <p className="text-white font-semibold">{partner.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Partners Section */}
      <section className="section-padding">
        <div className="container">
          <h2 className="text-4xl font-serif font-bold text-gold text-center mb-12">
            Další partneři
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {mockData.additionalPartners.map((partner, index) => (
              <div key={index} className="text-center">
                {partner.website && partner.website !== "#" ? (
                  <a 
                    href={partner.website}
                    target={partner.website.startsWith('tel:') ? '_self' : '_blank'}
                    rel="noopener noreferrer"
                    className="block hover:scale-105 transition-transform"
                  >
                    <div className="bg-white rounded-lg p-4 shadow-md mb-3 flex items-center justify-center min-h-[80px] hover:shadow-lg">
                      <img 
                        src={partner.logo} 
                        alt={partner.name} 
                        className="max-h-12 max-w-full w-auto object-contain"
                      />
                    </div>
                    <p className="text-white text-sm font-medium hover:text-gold transition-colors">
                      {partner.name}
                      {partner.website.startsWith('tel:') && (
                        <span className="block text-xs text-gold mt-1">
                          📞 {partner.website.replace('tel:', '')}
                        </span>
                      )}
                    </p>
                  </a>
                ) : (
                  <div>
                    <div className="bg-white rounded-lg p-4 shadow-md mb-3 flex items-center justify-center min-h-[80px]">
                      <img 
                        src={partner.logo} 
                        alt={partner.name} 
                        className="max-h-12 max-w-full w-auto object-contain"
                      />
                    </div>
                    <p className="text-white text-sm font-medium">{partner.name}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-dark-blue border-t border-gold/30 py-12">
        <div className="container">
          <div className="text-center">
            <div className="mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_4e5397d3-7819-4499-9fa9-6acd74b22875/artifacts/fcyppy6t_rozsvit_logo.png" 
                alt="Rozsviť Přerov Logo" 
                className="mx-auto w-16 h-16 mb-4"
              />
              <h3 className="text-2xl font-serif font-bold text-gold">Rozsviť Přerov</h3>
            </div>
            
            <div className="flex justify-center gap-8 mb-8 flex-wrap">
              <a 
                href="mailto:rozsvitprerov@seznam.cz"
                className="text-white/80 hover:text-gold transition-colors"
              >
                rozsvitprerov@seznam.cz
              </a>
              <a 
                href="http://rozsvitprerov.cz"
                className="text-white/80 hover:text-gold transition-colors"
              >
                rozsvitprerov.cz
              </a>
            </div>
            
            <div className="border-t border-gold/20 pt-8">
              <p className="text-white/60 text-sm">
                © 2025 rozsvitprerov.cz. Projekt realizován ve spolupráci s Aktivitka z.s.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;